import { parseStatusBoardFence } from '@/lib/status-board-fence'
import { cn } from '@/lib/utils'
import { testIds } from '@/lib/test-ids'

type StatusBoardFenceViewProps = {
  source: string
  className?: string
}

/** Human-readable Hot pickups / Blocked for a ```yaml status-board fence body. */
export function StatusBoardFenceView({ source, className }: StatusBoardFenceViewProps) {
  const parsed = parseStatusBoardFence(source)
  if (!parsed) {
    return (
      <div
        className={cn(
          'rounded-lg border border-dashed border-border bg-muted/30 px-3 py-2 text-sm text-muted-foreground',
          className,
        )}
        data-testid={testIds.statusBoardFence.root}
      >
        Status board fence (unparseable)
      </div>
    )
  }

  return (
    <div
      className={cn(
        'not-prose rounded-lg border border-border/50 bg-muted/20 px-3 py-3 my-2',
        className,
      )}
      data-testid={testIds.statusBoardFence.root}
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-foreground">
          Hot pickups · {parsed.hotPickups.length}
        </span>
        <span className="text-xs text-muted-foreground">
          Updated {parsed.lastBoardUpdate || '—'}
        </span>
      </div>
      {parsed.hotPickups.length === 0 ? (
        <p className="mb-2 text-xs text-muted-foreground">No hot pickups</p>
      ) : (
        <ul className="mb-2 divide-y divide-border/40" data-testid={testIds.statusBoardFence.hotPickups}>
          {parsed.hotPickups.map((row) => (
            <li key={`${row.item}-${row.noteDate}`} className="py-2">
              <div className="text-sm font-medium text-foreground">{row.item}</div>
              <div className="mt-0.5 text-xs text-muted-foreground">{row.next || '—'}</div>
              {(row.noteDate || row.bet) && (
                <div className="mt-0.5 text-[11px] text-muted-foreground/80">
                  {[row.noteDate, row.bet].filter(Boolean).join(' · ')}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {parsed.blocked.length > 0 && (
        <>
          <div className="mb-1 mt-2 text-xs font-semibold text-foreground">
            Blocked · {parsed.blocked.length}
          </div>
          <ul className="space-y-1" data-testid={testIds.statusBoardFence.blocked}>
            {parsed.blocked.map((row) => (
              <li key={row.item} className="text-xs text-muted-foreground">
                {row.item} - {row.blockedOn}
                {row.owner ? ` (${row.owner})` : ''}
              </li>
            ))}
          </ul>
        </>
      )}
      <p className="mt-2 text-[11px] text-muted-foreground">
        Edit the yaml status-board fence in Source mode
      </p>
    </div>
  )
}
